import { useQuery } from '@tanstack/react-query'

import { GoogleSignInButton } from '@/auth/GoogleSignInButton'
import { FxButton } from '@/components/fx'
import { authApi } from '@/lib/api'

/**
 * Which third-party sign-ins this deployment has configured.
 *
 * `authApi.oauthProviders` already swallows its own errors and answers with
 * an empty list, so there is nothing for a caller to handle: no providers
 * simply means no buttons, and the password form still works. Cached for the
 * session — a deployment does not gain a provider while someone is looking
 * at the sign-in screen.
 */
export function useOAuthProviders() {
  return useQuery({
    queryKey: ['oauth-providers'],
    queryFn: () => authApi.oauthProviders(),
    staleTime: Infinity,
    initialData: [] as string[],
  })
}

/** The "oder" rule the artboards draw between the password form and the
 *  provider buttons. Decorative, so it is hidden from the reader. */
export function OrDivider() {
  return (
    <div className="flex items-center gap-4 text-body text-text" aria-hidden="true">
      <span className="h-0.5 flex-1 bg-divider" />
      oder
      <span className="h-0.5 flex-1 bg-divider" />
    </div>
  )
}

export interface OAuthButtonsProps {
  providers: string[]
  /** Same-origin path the gateway sends the browser back to. */
  returnTo?: string
  onGoogleCredential: (idToken: string) => void
  /** "Mit Google anmelden" on sign-in, the shorter "Mit Google" in the funnel. */
  microsoftLabel?: string
  googleText?: 'signin_with' | 'signup_with' | 'continue_with'
  disabled?: boolean
}

/**
 * The provider buttons, stacked rather than in the artboards' two-column grid.
 *
 * Google Identity Services renders its own button and its width is a fixed
 * property of the shared `GoogleSignInButton` (320px); it cannot be squeezed
 * into a 220px half-column, and Google's branding terms do not let us draw
 * our own in its place. So the two buttons share one width and sit above each
 * other, which is also what the 390px layout needs anyway.
 */
export function OAuthButtons({
  providers,
  returnTo,
  onGoogleCredential,
  microsoftLabel = 'Mit Microsoft anmelden',
  googleText = 'signin_with',
  disabled = false,
}: OAuthButtonsProps) {
  const google = providers.includes('google')
  const microsoft = providers.includes('microsoft')
  if (!google && !microsoft) return null

  return (
    <div className="flex flex-col items-center gap-4">
      {google && (
        <div className="w-full max-w-[320px]">
          <GoogleSignInButton onCredential={onGoogleCredential} text={googleText} />
        </div>
      )}
      {microsoft && (
        <FxButton
          block
          disabled={disabled}
          // The artboards draw the provider buttons in the neutral `fill`, so
          // the one blue action on the page stays the form's own submit. The
          // design system has no neutral button variant; hover steps up the
          // tonal ladder like every other interactive surface.
          className="max-w-[320px] bg-fill text-text hover:bg-primary-subtle hover:text-primary-text"
          // The redirect flow leaves the page: the gateway is the confidential
          // client, parks state and the PKCE verifier in Redis, and comes back
          // to /auth/callback with a one-time code.
          onClick={() => {
            window.location.href = authApi.startUrl('microsoft', returnTo)
          }}
        >
          {microsoftLabel}
        </FxButton>
      )}
    </div>
  )
}
